/**
 * <cart-summary-actions-component>
 *
 * Buttons row + 3 slide-up modals (Versandkosten / Hinweis / Rabatt).
 *
 * Modals stay INSIDE the component (no relocation). The cart drawer re-renders
 * via morph on add-to-cart / quantity changes, so we never cache modal nodes —
 * we query them live from the DOM each time and rely on event delegation bound
 * to the component element (which survives morph) instead of per-node listeners.
 */

import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';

class CartSummaryActionsComponent extends Component {
  connectedCallback() {
    super.connectedCallback?.();
    // Delegated click handling: the listener lives on the component element,
    // which persists across cart-drawer morphs, so close / apply-note keep working
    // even after the modal markup inside is re-rendered.
    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.removeEventListener('click', this.#onClick);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  /** Live query of the modal nodes inside the component. */
  get #modals() {
    return /** @type {HTMLElement[]} */ (
      Array.from(this.querySelectorAll('.cart-summary-actions-modal'))
    );
  }

  /** @param {string} type */
  #findModal(type) {
    return /** @type {HTMLElement | null} */ (
      this.querySelector(`.cart-summary-actions-modal[data-modal-type="${type}"]`)
    );
  }

  /** @param {Event} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const trigger = target.closest('[data-action]');
    if (!(trigger instanceof HTMLElement)) return;

    const action = trigger.dataset.action;
    if (action === 'close-modal') {
      event.preventDefault();
      this.closeModal();
    } else if (action === 'apply-note') {
      event.preventDefault();
      this.#handleApplyNote(trigger);
    }
  };

  // ─── Button handlers (declarative on:click) ──────────────────────────

  /** @param {Event} event */
  openShippingModal(event) {
    event.preventDefault();
    this.#openModal('shipping');
  }

  /** @param {Event} event */
  openNoteModal(event) {
    event.preventDefault();
    this.#openModal('note');
  }

  /** @param {Event} event */
  openDiscountModal(event) {
    event.preventDefault();
    this.#openModal('discount');
  }

  /** @param {string} type */
  #openModal(type) {
    // Close any other open modal first (no stacking).
    for (const m of this.#modals) {
      if (!m.hidden && m.dataset.modalType !== type) {
        m.classList.remove('is-closing');
        m.hidden = true;
      }
    }

    const modal = this.#findModal(type);
    if (!modal) return;
    modal.hidden = false;

    if (type === 'shipping') {
      const calc = /** @type {any} */ (modal.querySelector('cart-shipping-calc-component'));
      calc?.activate?.();
    }

    // Defer focus until the slide-up animation finishes; use preventScroll so the
    // browser does not scroll the animating fixed panel to bring the input into view
    // (that's what caused the "jump" on Hinweis / Rabatt).
    if (type === 'note' || type === 'discount') {
      const focusTarget =
        type === 'note'
          ? modal.querySelector('[data-note-input]')
          : modal.querySelector('input[name="discount"]');

      if (focusTarget instanceof HTMLElement) {
        const focusOnce = () => {
          try {
            focusTarget.focus({ preventScroll: true });
          } catch {
            focusTarget.focus();
          }
        };
        const panel = modal.querySelector('.cart-summary-actions-modal__panel');
        if (panel instanceof HTMLElement) {
          panel.addEventListener('animationend', focusOnce, { once: true });
        } else {
          focusOnce();
        }
      }
    }
  }

  closeModal() {
    for (const modal of this.#modals) {
      if (modal.hidden) continue;
      modal.classList.add('is-closing');

      const calc = /** @type {any} */ (modal.querySelector('cart-shipping-calc-component'));
      calc?.deactivate?.();

      modal.addEventListener(
        'animationend',
        () => {
          modal.classList.remove('is-closing');
          modal.hidden = true;
        },
        { once: true }
      );
    }
  }

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (!this.#modals.some((m) => !m.hidden)) return;
    event.stopPropagation();
    this.closeModal();
  };

  /** @param {HTMLElement} btn */
  async #handleApplyNote(btn) {
    const modal = this.#findModal('note');
    if (!modal) return;
    const ta = modal.querySelector('[data-note-input]');
    if (!(ta instanceof HTMLTextAreaElement)) return;

    const button = btn instanceof HTMLButtonElement ? btn : null;
    if (button) button.disabled = true;
    btn.classList.add('is-loading');

    try {
      const config = fetchConfig('json', {
        body: JSON.stringify({ note: ta.value }),
      });
      const res = await fetch(Theme.routes.cart_update_url, config);
      if (!res.ok) throw new Error('cart-note update failed');
      this.closeModal();
    } catch (_err) {
      const errorEl = modal.querySelector('[data-note-error]');
      if (errorEl instanceof HTMLElement) errorEl.hidden = false;
    } finally {
      if (button) button.disabled = false;
      btn.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('cart-summary-actions-component')) {
  customElements.define('cart-summary-actions-component', CartSummaryActionsComponent);
}
